import type { ApiEmployee } from '@/types/employee.types';
import { create } from 'zustand';
import type {
  AuthUser,
  AuthTokens,
  V2LoginResponse,
  UserProfile,
  UserAuthority,
} from '@/types/auth.types';

interface AuthStore {
  authenticatedEmployee: ApiEmployee | null;
  authError: string | null;
  setAuthError: (error: string | null) => void;
  setGoogleAuth: (employee: ApiEmployee, profile: UserProfile, authority: UserAuthority) => void;
  user: AuthUser | null;
  tokens: AuthTokens | null;
  v2User: V2LoginResponse | null;
  userProfile: UserProfile | null;
  authority: UserAuthority | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setAuth: (user: AuthUser, tokens: AuthTokens) => void;
  setV2Auth: (v2User: V2LoginResponse) => void;
  setUser: (user: AuthUser) => void;
  setUserProfile: (profile: UserProfile) => void;
  setAuthority: (authority: UserAuthority) => void;
  clearAuth: () => void;
  updateAuthenticatedEmployee: (patch: Partial<ApiEmployee>) => void;
  /**
   * Clear identity fields without exiting the loading state, so route guards
   * keep showing the loading screen while a restored session is re-validated.
   */
  resetIdentity: () => void;
  setLoading: (isLoading: boolean) => void;
}

export const useAuthStore = create<AuthStore>(set => ({
  authenticatedEmployee: null,
  authError: null,
  setAuthError: authError => set({ authError }),
  setGoogleAuth: (authenticatedEmployee, userProfile, authority) =>
    set({
      authenticatedEmployee,
      userProfile,
      authority,
      v2User: null,
      tokens: null,
      authError: null,
      user: {
        id: String(authenticatedEmployee.id),
        email: userProfile.primaryEmail,
        firstName: userProfile.firstname,
        lastName: userProfile.lastname,
        avatar: authenticatedEmployee.photo,
        employeeRole: authority.roles[0],
        permissions: authority.permissions,
        organizationId: authenticatedEmployee.organization ?? '',
        isEmailVerified: true,
      },
      isAuthenticated: true,
      isLoading: false,
    }),
  user: null,
  tokens: null,
  v2User: null,
  userProfile: null,
  authority: null,
  isAuthenticated: false,
  isLoading: true,
  setAuth: (user, tokens) => set({ user, tokens, isAuthenticated: true, isLoading: false }),
  setV2Auth: v2User =>
    set({
      v2User,
      isAuthenticated: true,
      isLoading: false,
    }),
  setUser: user => set({ user }),
  setUserProfile: userProfile => set({ userProfile }),
  setAuthority: authority => set({ authority }),
  updateAuthenticatedEmployee: patch =>
    set(state => ({
      authenticatedEmployee: state.authenticatedEmployee
        ? { ...state.authenticatedEmployee, ...patch }
        : null,
      user: state.user
        ? {
            ...state.user,
            ...(patch.photo ? { avatar: patch.photo } : {}),
          }
        : null,
    })),
  clearAuth: () =>
    set({
      authenticatedEmployee: null,
      user: null,
      tokens: null,
      v2User: null,
      userProfile: null,
      authority: null,
      isAuthenticated: false,
      isLoading: false,
    }),
  resetIdentity: () =>
    set({
      authenticatedEmployee: null,
      user: null,
      tokens: null,
      v2User: null,
      userProfile: null,
      authority: null,
      isAuthenticated: false,
    }),
  setLoading: isLoading => set({ isLoading }),
}));
